// DS: C118
import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import { PopupContext } from '@wix/da-ds/pkg/popups/PopupContext';
import CloseButton from '../../Button/CloseButton';
import UserSearchField from '../';
import noop from '../../utils/noop';
import s from './UserInput.scss';

export interface Props {
  users: PapiUser[];
  candidates?: PapiUser[];
  className?: string;
  scrollContainerClassName?: string;
  prefix?: React.ReactNode;
  placeholder?: string;
  onKeyDown?: (e) => void;
  onAddUser: (user: PapiUser) => void;
  onRemoveUser: (user: PapiUser) => void;
  onNoMatchingUsers?: (input: string) => void;
  onRemoveAll: () => void;
  withClearButton?: boolean;
  /** Users to exclude from the user search in addition to those in `users` */
  additionalExcludedUsers?: PapiUser[];
  disabled?: boolean;
}

export const UserInput: React.FC<Props> = ({
  users,
  className,
  candidates,
  scrollContainerClassName,
  prefix,
  placeholder,
  onAddUser,
  onRemoveUser,
  onRemoveAll,
  onKeyDown,
  onNoMatchingUsers,
  withClearButton,
  disabled,
  additionalExcludedUsers,
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const renderUser = (user: PapiUser, index: number) => (
    <div className={classnames(s['item'], s['item-flex'])} key={`selected-${user.userId}-${index}`}>
      {user.username}
      <CloseButton
        className={s['close-button']}
        view="b5-3"
        onClick={e => {
          onRemoveUser(user);
          e.stopPropagation();
        }}
      />
    </div>
  );
  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLInputElement;
      if (e.key === 'Backspace' && target.value.length === 0 && users.length > 0) {
        onRemoveUser(users[users.length - 1]);
      }
      onKeyDown && onKeyDown(e);
    },
    [users, onRemoveUser, onKeyDown]
  );

  const mergedExclusiveUsers = useMemo(
    () => [...users, ...(additionalExcludedUsers || [])],
    [users, additionalExcludedUsers]
  );

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [users.length]);

  return (
    <div
      className={classnames(s['root'], className)}
      onClick={e => inputRef.current && inputRef.current.focus()}
    >
      {prefix && (
        // We're expecting the client to provide a label for this field, so hide this prefix to avoid
        // additional noise
        <span className={s['prefix']} aria-hidden="true">
          {prefix}
        </span>
      )}
      <Flex
        className={classnames(
          s['item-container'] as any,
          prefix && s['has-prefix'],
          scrollContainerClassName
        )}
        alignItems="flex-start"
        wrap="wrap"
        innerRef={scrollAreaRef}
      >
        {users.map(renderUser)}
        <PopupContext.Provider value={{ isInPopup: true, setScrollableContentRef: noop }}>
          <UserSearchField
            key="searchField"
            candidates={candidates}
            onUserSelect={onAddUser}
            excludedUsers={mergedExclusiveUsers}
            className={classnames(s['user-input'], s['item-flex'])}
            inputClassName={s['user-input-textfield']}
            placeholder={placeholder}
            inputRef={inputRef}
            withClearButton={withClearButton}
            onClear={onRemoveAll}
            onInputKeyDown={onInputKeyDown}
            onNoMatchingUsers={onNoMatchingUsers}
            popupFixedPosition
            isChatContext
            disabled={disabled}
          />
        </PopupContext.Provider>
      </Flex>
    </div>
  );
};
UserInput.displayName = 'UserInput';

export default UserInput;
